import { useMemo, useState } from "react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { CalendarClock, MapPin, Trophy } from "lucide-react";
import { api } from "@/lib/api";
import { useAuthStore } from "@/store/auth";
import type { PaginatedResult, RefereeAssignedMatch } from "@/types";
import { MATCH_STATUS_LABEL, championshipTone, formatDateTime } from "@/lib/championships";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Card, Badge } from "@/components/ui/Card";
import { Pagination } from "@/components/ui/Pagination";
import { ExportExcelButton } from "@/components/ui/ExportExcelButton";
import { TableSkeleton } from "@/components/ui/Skeleton";

type EstadoFilter = "PENDIENTES" | "" | RefereeAssignedMatch["estado"];

const selectClass =
  "w-full rounded-xl border border-ink-100 bg-white px-3 py-2 text-sm outline-none transition focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20";

// Inicio de una cuenta de árbitro: los partidos de campeonato que le han asignado (la tablet de
// árbitro sigue entrando con el código del partido, sin relación con esta cuenta — ese cambio queda
// para más adelante). Un árbitro de un torneo grande puede tener decenas de partidos: el listado va
// paginado y con filtros, y por defecto solo muestra los pendientes.
export function RefereeHomePage() {
  const user = useAuthStore((s) => s.user);

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [searchInput, setSearchInput] = useState("");
  const search = useDebouncedValue(searchInput.trim());
  const [estado, setEstado] = useState<EstadoFilter>("PENDIENTES");

  const filterParams = useMemo(() => {
    const params = new URLSearchParams();
    if (search) params.set("search", search);
    if (estado) params.set("estado", estado);
    return params;
  }, [search, estado]);

  const { data, isLoading } = useQuery({
    queryKey: ["championships", "mine", "referee", page, limit, filterParams.toString()],
    queryFn: async () => {
      const params = new URLSearchParams(filterParams);
      params.set("page", String(page));
      params.set("limit", String(limit));
      return (await api.get<PaginatedResult<RefereeAssignedMatch>>(`/championships/mine/referee?${params.toString()}`)).data;
    },
    placeholderData: keepPreviousData,
  });

  const rows = data?.data ?? [];
  const hasFilters = Boolean(searchInput || estado !== "PENDIENTES");

  return (
    <div className="flex flex-col gap-8">
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-brand-700 via-brand-600 to-brand-800 p-5 text-white shadow-lg shadow-brand-900/20 sm:p-7">
        <div className="pointer-events-none absolute -top-16 -right-16 h-56 w-56 rounded-full bg-white/10" />
        <div className="relative">
          <p className="text-sm font-medium tracking-wide text-brand-100 uppercase">Cuenta de árbitro</p>
          <h1 className="mt-1 text-2xl font-bold tracking-tight break-words sm:text-3xl">Hola, {user?.nombre?.split(" ")[0] ?? ""}</h1>
          <p className="mt-2 max-w-md text-sm text-brand-50/90">Aquí verás los partidos de campeonato en los que te han asignado como árbitro.</p>
        </div>
      </div>

      <Card className="flex flex-col gap-4">
        <div className="flex items-center gap-2">
          <Trophy className="h-4 w-4 text-brand-600" />
          <h2 className="font-semibold text-ink-900">Mis partidos asignados</h2>
        </div>

        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
          <div className="col-span-2 sm:col-span-1">
            <Input
              label="Buscar"
              placeholder="Campeonato, pareja, pista…"
              value={searchInput}
              onChange={(e) => {
                setSearchInput(e.target.value);
                setPage(1);
              }}
              className="w-full"
            />
          </div>
          <div className="col-span-2 flex flex-col gap-1.5 sm:col-span-1">
            <label className="text-sm font-medium text-ink-600">Estado</label>
            <select
              value={estado}
              onChange={(e) => {
                setEstado(e.target.value as EstadoFilter);
                setPage(1);
              }}
              className={selectClass}
            >
              <option value="PENDIENTES">Pendientes</option>
              <option value="">Todos</option>
              {(Object.keys(MATCH_STATUS_LABEL) as RefereeAssignedMatch["estado"][]).map((s) => (
                <option key={s} value={s}>
                  {MATCH_STATUS_LABEL[s]}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="flex flex-wrap items-center justify-between gap-3">
          {hasFilters ? (
            <Button
              onClick={() => {
                setSearchInput("");
                setEstado("PENDIENTES");
                setPage(1);
              }}
            >
              Limpiar filtros
            </Button>
          ) : (
            <span />
          )}
          <ExportExcelButton url={`/championships/mine/referee/export.xlsx?${filterParams.toString()}`} filename="mis-partidos.xlsx" />
        </div>

        {isLoading ? (
          <TableSkeleton rows={3} cols={3} />
        ) : !rows.length ? (
          <p className="text-sm text-ink-400">
            {hasFilters ? "Ningún partido coincide con los filtros." : "No tienes ningún partido pendiente."}
          </p>
        ) : (
          <ul className="flex flex-col gap-2">
            {rows.map((m) => (
              <li key={m.id} className="flex flex-col gap-1.5 rounded-xl border border-ink-100 p-3">
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <p className="min-w-0 text-sm font-semibold break-words text-ink-800">
                    {m.campeonato.nombre}
                    {m.fase && <span className="font-normal text-ink-400"> · {m.fase}</span>}
                  </p>
                  <Badge tone={championshipTone(m.estado)}>{MATCH_STATUS_LABEL[m.estado]}</Badge>
                </div>
                <p className="text-sm break-words text-ink-600">
                  {m.equipoA ?? "Por definir"} <span className="text-ink-300">vs</span> {m.equipoB ?? "Por definir"}
                </p>
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-ink-400">
                  <span className="inline-flex items-center gap-1.5">
                    <CalendarClock className="h-3.5 w-3.5 shrink-0" />
                    {formatDateTime(m.startsAt)}
                  </span>
                  <span className="inline-flex min-w-0 items-center gap-1.5">
                    <MapPin className="h-3.5 w-3.5 shrink-0" />
                    <span className="truncate">{m.court ? `${m.court.club.nombre} — ${m.court.name}` : "Pista por definir"}</span>
                  </span>
                </div>
              </li>
            ))}
          </ul>
        )}

        <Pagination
          page={data?.page ?? 1}
          totalPages={data?.totalPages ?? 1}
          total={data?.total ?? 0}
          onPageChange={setPage}
          limit={limit}
          onLimitChange={(l) => {
            setLimit(l);
            setPage(1);
          }}
        />
      </Card>
    </div>
  );
}
